import React, { useRef, useState } from "react";
import html2canvas from "html2canvas";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { AppHeader } from "./CommunitySelection";
import { useLang } from "../lib/i18n";
import { ArrowDown, HeartOff, AlertTriangle, HandHeart, ArrowRight, Share2 } from "lucide-react";

const ListCard = ({ icon: Icon, color, title, items, empty }) => (
  <div className="bg-white rounded-lg border border-[var(--line)] p-6">
    <div className="flex items-center gap-2 mb-4" style={{ color }}>
      <Icon className="w-4 h-4" strokeWidth={1.75} />
      <span className="text-[11px] uppercase tracking-[0.14em] font-semibold">{title}</span>
    </div>
    {items.length ? (
      <ul className="space-y-2">
        {items.map((it) => (
          <li key={it.key} className="text-sm text-ink flex items-start gap-2">
            <span className="mt-1.5 h-1 w-1 rounded-full shrink-0" style={{ backgroundColor: color }} />
            {it.label}
          </li>
        ))}
      </ul>
    ) : (
      <p className="text-sm text-ink-soft">{empty}</p>
    )}
  </div>
);

export const JourneyEnding = ({ summary, onContinue, quick = false, deepLoading = false }) => {
  const { t } = useLang();
  const profile = summary.ending_profile;
  const shareRef = useRef(null);
  const [sharing, setSharing] = useState(false);
  const appUrl = `${window.location.origin}/`;

  const shareYear = async () => {
    if (!shareRef.current) return;
    setSharing(true);
    try {
      const canvas = await html2canvas(shareRef.current, {
        scale: 1,
        useCORS: true,
        backgroundColor: "#2C2117",
        logging: false,
      });
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      const file = new File([blob], "root-causes-year.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: t("Root Causes Challenge"),
          text: t("A year in the shoes of a neighbor experiencing poverty — Root Causes Challenge."),
        });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "root-causes-year.png";
        a.click();
        URL.revokeObjectURL(url);
        toast.success(t("Image saved — share it wherever you like."));
      }
    } catch (e) {
      if (e && e.name === "AbortError") return;
      toast.error(t("Could not create the image. Please try again."));
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="min-h-screen pb-28">
      <AppHeader subtitle={summary.community_name} />
      {/* Off-screen branded share card rendered to an image on demand. */}
      <div style={{ position: "fixed", top: 0, left: "-9999px", pointerEvents: "none" }} aria-hidden="true">
        <div
          ref={shareRef}
          data-testid="share-card"
          style={{
            width: "1080px",
            padding: "72px",
            backgroundColor: "#2C2117",
            color: "#ffffff",
            fontFamily: "Arial, Helvetica, sans-serif",
            boxSizing: "border-box",
          }}
        >
          <div style={{ fontSize: "22px", letterSpacing: "4px", textTransform: "uppercase", color: "rgba(255,255,255,0.7)", fontWeight: 700 }}>
            {t("Root Causes Challenge")} · {t("An Interactive Poverty Simulation")}
          </div>
          <div style={{ fontSize: "64px", fontWeight: 700, marginTop: "28px", lineHeight: 1.1 }}>
            {t("This neighbor's year")}
          </div>
          <div style={{ fontSize: "30px", color: "rgba(255,255,255,0.85)", marginTop: "18px" }}>
            {summary.family?.label} · {summary.community_name}
          </div>

          {profile && (
            <div style={{ marginTop: "44px", borderLeft: `10px solid ${profile.color}`, paddingLeft: "28px" }}>
              <div style={{ fontSize: "24px", letterSpacing: "3px", textTransform: "uppercase", fontWeight: 700, color: "#ffffff" }}>
                {profile.eyebrow}
              </div>
              <div style={{ fontSize: "46px", fontWeight: 700, marginTop: "10px", lineHeight: 1.15 }}>
                {profile.headline}
              </div>
            </div>
          )}

          {summary.borrowed > 0 && (
            <div style={{ marginTop: "36px", backgroundColor: "rgba(255,255,255,0.08)", borderRadius: "16px", padding: "28px 32px" }}>
              <div style={{ fontSize: "22px", letterSpacing: "2px", textTransform: "uppercase", color: "#F2B8B8", fontWeight: 700 }}>
                {t("The high cost of borrowing")}
              </div>
              <div style={{ fontSize: "30px", marginTop: "12px", lineHeight: 1.35 }}>
                {t("Borrowed")} ${summary.borrowed.toLocaleString()} · {t("paying")} ${summary.monthly_debt}{t("/mo")} · {t("about")} ${summary.annual_debt.toLocaleString()} {t("a year — roughly")} {summary.debt_multiple}{t("× what was borrowed.")}
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: "48px", marginTop: "52px" }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "24px", letterSpacing: "2px", textTransform: "uppercase", color: "#F2B8B8", fontWeight: 700 }}>
                {t("Barriers faced")}
              </div>
              <div style={{ marginTop: "18px" }}>
                {summary.barriers.slice(0, 4).map((b) => (
                  <div key={b.key} style={{ fontSize: "28px", marginBottom: "12px", lineHeight: 1.3 }}>• {b.label}</div>
                ))}
                {summary.barriers.length === 0 && (
                  <div style={{ fontSize: "28px" }}>{t("A relentless string of trade-offs.")}</div>
                )}
              </div>
              {summary.supports.length > 0 && (
                <div style={{ marginTop: "34px" }}>
                  <div style={{ fontSize: "24px", letterSpacing: "2px", textTransform: "uppercase", color: "#A8D5C2", fontWeight: 700 }}>
                    {t("What helped")}
                  </div>
                  <div style={{ marginTop: "18px" }}>
                    {summary.supports.map((s) => (
                      <div key={s.key} style={{ fontSize: "28px", marginBottom: "12px", lineHeight: 1.3 }}>• {s.label}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div style={{ width: "260px", textAlign: "center" }}>
              <div style={{ backgroundColor: "#ffffff", padding: "18px", borderRadius: "16px", display: "inline-block" }}>
                <QRCodeCanvas value={appUrl} size={220} fgColor="#2C2117" level="M" includeMargin={false} />
              </div>
              <div style={{ fontSize: "24px", fontWeight: 700, marginTop: "20px" }}>{t("Scan to walk a year")}</div>
              <div style={{ fontSize: "20px", color: "rgba(255,255,255,0.75)", marginTop: "8px" }}>{t("in a neighbor's shoes")}</div>
            </div>
          </div>

          <div style={{ fontSize: "20px", color: "rgba(255,255,255,0.6)", marginTop: "56px", borderTop: "1px solid rgba(255,255,255,0.2)", paddingTop: "24px" }}>
            {t("Root Causes Challenge")} · {t("Designed and Developed by Matambanadzo Studio")}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-10 sm:py-12">
        {profile && (
          <div
            data-testid="ending-profile"
            className="animate-rise mb-8 rounded-lg border p-6 sm:p-8"
            style={{ borderColor: profile.color, backgroundColor: `${profile.color}0D` }}
          >
            <p
              className="text-[11px] uppercase tracking-[0.18em] font-semibold mb-2"
              style={{ color: profile.color }}
            >
              {profile.eyebrow}
            </p>
            <h2
              data-testid="ending-profile-headline"
              className="font-display text-2xl sm:text-3xl font-semibold leading-tight"
              style={{ color: profile.color }}
            >
              {profile.headline}
            </h2>
            <p className="mt-4 text-[15px] text-ink leading-relaxed">{profile.reflection}</p>
            {summary.monthly_debt > 0 && (
              <div
                data-testid="ending-profile-debt"
                className="mt-5 rounded-lg bg-white p-5"
                style={{ border: `1px solid ${profile.color}` }}
              >
                {summary.borrowed > 0 ? (
                  <>
                    <div className="text-[11px] uppercase tracking-[0.14em] font-semibold" style={{ color: profile.color }}>
                      {t("The high cost of borrowing")}
                    </div>
                    <p className="mt-2 text-[15px] text-ink leading-relaxed">
                      {t("Borrowed")} <span className="font-semibold">${summary.borrowed.toLocaleString()}</span> {t("to get through the year — now paying")}{" "}
                      <span className="font-semibold">${summary.monthly_debt}{t("/month")}</span>. {t("Over a year that's about")}{" "}
                      <span className="font-semibold" style={{ color: profile.color }}>${summary.annual_debt.toLocaleString()}</span>{t(", roughly")}{" "}
                      <span className="font-semibold" style={{ color: profile.color }}>{summary.debt_multiple}×</span> {t("what was borrowed.")}
                    </p>
                  </>
                ) : (
                  <span className="text-sm font-semibold" style={{ color: profile.color }}>
                    +${summary.monthly_debt}{t("/mo in loan payments now owed — every month, indefinitely")}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
        <div className="animate-rise">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-vin/70 mb-3">
            {quick ? t("A brief glimpse") : t("One year, one decision at a time")}
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-ink leading-tight">
            {quick ? t("A glimpse of this neighbor's reality") : t("This neighbor's year")}
          </h2>
          <p className="mt-4 text-base text-ink-soft leading-relaxed">
            {quick
              ? t("In just a few decisions, you already felt how quickly one hardship pulls the next one closer. This was only a snapshot — a full year holds many more of these impossible trade-offs.")
              : t("There was no winning or losing here — only a series of impossible trade-offs. Each choice was, most likely, the best one available in the moment. This is the reality so many families live every day.")}
          </p>
        </div>

        <div className="mt-8 bg-white rounded-lg border border-[var(--line)] p-6 sm:p-8">
          <div className="space-y-0">
            {summary.chain.map((c, i) => (
              <div key={i} className="animate-rise" style={{ animationDelay: `${i * 55}ms` }}>
                <div className="flex items-start gap-4 py-1">
                  <span className="mt-1 h-2.5 w-2.5 rounded-full bg-vin shrink-0" />
                  <div>
                    <div className="font-display font-semibold text-ink">{c.node}</div>
                    <div className="text-xs text-ink-soft">{c.title}</div>
                  </div>
                </div>
                {i < summary.chain.length - 1 && (
                  <div className="pl-[3px]"><ArrowDown className="w-4 h-4 text-vin/40" strokeWidth={2} /></div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <ListCard
            icon={AlertTriangle}
            color="#8E2929"
            title={t("Barriers encountered")}
            items={summary.barriers}
            empty={t("Few systemic barriers this time.")}
          />
          <ListCard
            icon={HeartOff}
            color="#5A2A85"
            title={t("What this family went without")}
            items={summary.sacrifices}
            empty={t("Few needs had to be sacrificed.")}
          />
        </div>

        {summary.supports.length > 0 && (
          <div className="mt-5">
            <ListCard
              icon={HandHeart}
              color="#365C4C"
              title={t("What helped")}
              items={summary.supports}
              empty=""
            />
          </div>
        )}
      </div>

      <div className="fixed bottom-0 inset-x-0 bg-white/95 border-t border-[var(--line)] backdrop-blur">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 py-4 flex items-center justify-between gap-3">
          <button
            data-testid="share-year-button"
            onClick={shareYear}
            disabled={sharing}
            className="inline-flex items-center gap-2 text-vin font-semibold px-5 py-3.5 rounded-full border border-vin/30 hover:bg-vin/5 transition-colors duration-200 disabled:opacity-60"
          >
            <Share2 className="w-4 h-4" strokeWidth={1.75} />
            {sharing ? t("Preparing…") : quick ? t("Share this snapshot") : t("Share this year")}
          </button>
          <button
            data-testid="journey-continue-button"
            onClick={onContinue}
            disabled={deepLoading}
            className="inline-flex items-center gap-2 bg-vin text-white font-semibold px-7 py-3.5 rounded-full hover:bg-vin-hover transition-colors duration-200 disabled:opacity-60"
          >
            {quick ? (deepLoading ? t("Starting…") : t("Walk a full year in their footsteps")) : t("Understand the community")}
            <ArrowRight className="w-5 h-5" strokeWidth={1.75} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default JourneyEnding;
